import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Checkbox from 'js/components/Forms/Checkbox';

/**
 * filterOptions: [{ value, text, defaultSelected }]; options start checked unless
 * defaultSelected is false.
 */
const DropdownFilter = ({
  name = 'Dropdown',
  filterOptions = [],
  onChange = () => {},
  className,
  renderToggle = null,
}) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // The default selection can be empty, so track that it was applied rather than checking
  // whether anything is selected (which would keep re-applying an empty default)
  const defaultsAppliedRef = useRef(false);

  // Apply the default selection once options are available
  useEffect(() => {
    if (
      filterOptions.length > 0 &&
      !hasUserInteracted &&
      !defaultsAppliedRef.current
    ) {
      defaultsAppliedRef.current = true;
      const defaultSelected = filterOptions.reduce((acc, option) => {
        if (option.defaultSelected !== false) {
          acc[option.value] = true;
        }
        return acc;
      }, {});
      setSelectedOptions(defaultSelected);
      onChange(defaultSelected);
    }
  }, [filterOptions, onChange, hasUserInteracted]);
  const [selectedOptionCount, allOptionsSelected] = useMemo(() => {
    const selectedOptionCount = filterOptions.filter(
      (o) => !!selectedOptions[o.value]
    ).length;

    return [
      selectedOptionCount,
      selectedOptionCount === 0 || selectedOptionCount === filterOptions.length,
    ];
  }, [filterOptions, selectedOptions]);

  const toggleDropdown = useCallback(() => {
    const newIsOpen = !dropdownOpen;
    setDropdownOpen(newIsOpen);
  }, [dropdownOpen, setDropdownOpen]);
  const toggleOption = useCallback(
    ({ value }) => {
      setHasUserInteracted(true); // Mark that user has interacted

      let newOptions = {
        ...selectedOptions,
        [value]: !selectedOptions[value],
      };

      const selectedOptionCount = filterOptions.filter(
        (o) => !!newOptions[o.value]
      ).length;

      const noOptionsSelected = selectedOptionCount === 0;

      newOptions = noOptionsSelected ? {} : newOptions;
      setSelectedOptions(newOptions);
      onChange(newOptions);
    },
    [filterOptions, selectedOptions, setSelectedOptions, onChange]
  );
  const setAllOptions = useCallback(() => {
    setHasUserInteracted(true); // Mark that user has interacted

    const newOptions =
      allOptionsSelected && selectedOptionCount > 0
        ? {}
        : filterOptions.reduce((acc, { value }) => {
            acc[value] = true;
            return acc;
          }, {});
    setSelectedOptions(newOptions);
    onChange(selectedOptionCount === filterOptions.length ? {} : newOptions);
  }, [
    allOptionsSelected,
    selectedOptionCount,
    filterOptions,
    setSelectedOptions,
    onChange,
  ]);

  return (
    <Dropdown
      isOpen={dropdownOpen}
      toggle={toggleDropdown}
      className={className}
    >
      {renderToggle ? (
        renderToggle()
      ) : (
        <DropdownToggle caret size="sm">
          {allOptionsSelected ? (
            name
          ) : (
            <b>
              {name} ({selectedOptionCount})
            </b>
          )}
        </DropdownToggle>
      )}
      <DropdownMenu>
        <DropdownItem
          toggle={false}
          tag="div"
          className="dropdown-item"
          onClick={setAllOptions}
        >
          <div style={{ pointerEvents: 'none', margin: '0 -1rem' }}>
            <Checkbox
              checked={allOptionsSelected && selectedOptionCount > 0}
              indeterminate={
                selectedOptionCount > 0 &&
                selectedOptionCount < filterOptions.length
              }
              onChange={() => {}}
            >
              (Select All)
            </Checkbox>
          </div>
        </DropdownItem>
        {filterOptions.map((o) => (
          <DropdownItem
            divider={false}
            key={o.value}
            toggle={false}
            tag="div"
            className="dropdown-item"
            onClick={() => toggleOption(o)}
          >
            <div style={{ pointerEvents: 'none', margin: '0 -1rem' }}>
              <Checkbox
                checked={!!selectedOptions[o.value]}
                onChange={() => {}}
              >
                {o.text}
              </Checkbox>
            </div>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

export default DropdownFilter;
